import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCheck,
  MessageSquare,
  Bot,
  Wrench,
  Drama,
  Settings,
  Flag,
  ClipboardList,
  FlaskConical,
  Mail,
  BarChart3,
  Search,
  ShieldCheck,
  Shield,
  UserPlus,
  DollarSign,
  Gauge,
  Brain,
  Activity,
  Heart,
  ShieldAlert,
  MessagesSquare,
  BookOpen,
} from "lucide-react";
import type { AdminPermission } from "@/services/api/admin-registry";

// ---------------------------------------------------------------------------
// Route metadata — single source of truth for sidebar, router guards,
// breadcrumbs, and command palette (Ctrl+K)
// ---------------------------------------------------------------------------

export interface RouteMeta {
  path: string;
  label: string;
  labelKey: string; // i18n key in admin namespace
  icon: LucideIcon;
  permission: AdminPermission;
  sectionKey?: string; // maps to AdminSection.key from /admin/me
  tab: 0 | 1 | 2 | 3;
  subtle?: boolean;
}

export const SIDEBAR_TABS = [
  { id: 0, label: "Operativo", icon: LayoutDashboard },
  { id: 1, label: "Qualita", icon: FlaskConical },
  { id: 2, label: "Agentic", icon: Bot },
  { id: 3, label: "System", icon: Shield },
] as const;

export const ROUTES_META: RouteMeta[] = [
  // -- Tab 0: Operativo --
  {
    path: "/dashboard",
    label: "Dashboard",
    labelKey: "nav.dashboard",
    icon: LayoutDashboard,
    permission: "dashboard:read",
    sectionKey: "dashboard",
    tab: 0,
  },
  {
    path: "/access-requests",
    label: "Richieste Accesso",
    labelKey: "nav.access_requests",
    icon: UserCheck,
    permission: "users:write",
    sectionKey: "users",
    tab: 0,
  },
  {
    path: "/tenants",
    label: "Tenants & Users",
    labelKey: "nav.tenants",
    icon: Building2,
    permission: "tenants:read",
    sectionKey: "tenants",
    tab: 0,
  },
  {
    path: "/users",
    label: "Utenti",
    labelKey: "nav.users",
    icon: Users,
    permission: "users:read",
    sectionKey: "users",
    tab: 0,
    subtle: true,
  },
  {
    path: "/conversations",
    label: "Conversazioni",
    labelKey: "nav.conversations",
    icon: MessageSquare,
    permission: "conversations:read",
    sectionKey: "conversations",
    tab: 0,
    subtle: true,
  },
  {
    path: "/library",
    label: "Libreria Privata",
    labelKey: "nav.library",
    icon: BookOpen,
    permission: "admin:read",
    tab: 0,
  },

  // -- Tab 1: Qualita --
  {
    path: "/insights/onboarding",
    label: "Onboarding Funnel",
    labelKey: "nav.insights_onboarding",
    icon: UserPlus,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/insights/economics",
    label: "Economics",
    labelKey: "nav.insights_economics",
    icon: DollarSign,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/insights/performance",
    label: "Performance",
    labelKey: "nav.insights_performance",
    icon: Gauge,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/insights/models",
    label: "Model Intelligence",
    labelKey: "nav.insights_models",
    icon: Brain,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/insights/activity",
    label: "Activity",
    labelKey: "nav.insights_activity",
    icon: Activity,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/insights/engagement",
    label: "User Engagement",
    labelKey: "nav.insights_engagement",
    icon: Heart,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/insights/quality",
    label: "Response Quality",
    labelKey: "nav.insights_quality",
    icon: ShieldAlert,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/insights/conversations",
    label: "Conversations",
    labelKey: "nav.insights_conversations",
    icon: MessagesSquare,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/assessment",
    label: "Assessment",
    labelKey: "nav.assessment",
    icon: BarChart3,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/bench",
    label: "Test & Benchmark",
    labelKey: "nav.bench",
    icon: FlaskConical,
    permission: "admin:read",
    tab: 1,
  },
  {
    path: "/trace",
    label: "Trace Viewer",
    labelKey: "nav.trace",
    icon: Search,
    permission: "admin:read",
    tab: 1,
  },

  // -- Tab 2: Agentic --
  {
    path: "/models",
    label: "Modelli LLM",
    labelKey: "nav.models",
    icon: Bot,
    permission: "models:read",
    sectionKey: "models",
    tab: 2,
  },
  {
    path: "/personas",
    label: "Personas",
    labelKey: "nav.personas",
    icon: Drama,
    permission: "personas:read",
    sectionKey: "personas",
    tab: 2,
  },
  {
    path: "/tools",
    label: "Strumenti",
    labelKey: "nav.tools",
    icon: Wrench,
    permission: "tools:read",
    sectionKey: "tools",
    tab: 2,
  },

  // -- Tab 3: System --
  {
    path: "/feature-flags",
    label: "Feature Flags",
    labelKey: "nav.feature_flags",
    icon: Flag,
    permission: "feature_flags:read",
    sectionKey: "feature_flags",
    tab: 3,
  },
  {
    path: "/audit",
    label: "Registro Attivita",
    labelKey: "nav.audit",
    icon: ClipboardList,
    permission: "audit:read",
    sectionKey: "audit",
    tab: 3,
  },
  {
    path: "/email-log",
    label: "Email Log",
    labelKey: "nav.email_log",
    icon: Mail,
    permission: "settings:read",
    tab: 3,
  },
  {
    path: "/settings",
    label: "Impostazioni",
    labelKey: "nav.settings",
    icon: Settings,
    permission: "settings:read",
    sectionKey: "settings",
    tab: 3,
  },
  {
    path: "/consent",
    label: "Consensi",
    labelKey: "nav.consent",
    icon: ShieldCheck,
    permission: "settings:read",
    tab: 3,
  },
];

/**
 * Get route meta by path.
 */
export function getRouteMeta(path: string): RouteMeta | undefined {
  return ROUTES_META.find((r) => r.path === path);
}

/**
 * Get routes filtered by tab index.
 */
export function getRoutesByTab(tab: number): RouteMeta[] {
  return ROUTES_META.filter((r) => r.tab === tab);
}
